import { RECEIVE_MODES, type CurrentUser, type ReceiveMode } from "@catmail/shared";
import {
  Bell,
  BellOff,
  Check,
  ChevronDown,
  ChevronRight,
  ImagePlus,
  Inbox,
  KeyRound,
  LogOut,
  Pencil,
  Plus,
  Settings2,
  ShieldCheck,
} from "lucide-react";
import { useState, type ReactNode } from "react";

import { Avatar, Badge, IconButton, ListRow, Popover } from "@/components";
import type { PushState } from "@/features/mail/hooks/usePushNotifications";
import { FOCUS, PRESS_FLAT } from "@/lib/interaction";
import { NARROW_QUERY, useMediaQuery } from "@/lib/useMediaQuery";
import { DROP_IN } from "@/lib/motion";

/**
 * 새 메일이 어디로 올지. 고른 값이 그대로 단추에 뜬다.
 *
 * **설정값이므로 진행형으로 쓰지 않는다.** 예전에는 "지금은 다 받는 중" /
 * "아는 사람만 받는 중" / "지금은 다 조용히"였다. 셋이 각각 다른 꼴이라 한 벌로
 * 읽히지 않았고, "지금은 ~하는 중"은 내가 정해둔 것이 아니라 앱이 지금 뭘 하고
 * 있다는 중계처럼 들렸다. 고르는 자리인 줄 모르고 지나치게 된다.
 *
 * 이름은 **화면에 실제로 보이는 것**을 쓴다. 가운데 흰 패널이 대화 목록이고,
 * 레일 아래 탭이 조용한 방이다. 예전 문구의 "받은 방"은 앱 어디에도 없는
 * 이름이라, 읽어도 어디를 말하는지 알 수 없었다.
 */
const RECEIVE_LABEL: Record<ReceiveMode, string> = {
  all: "모두 대화 목록으로",
  known: "아는 사람만 대화 목록으로",
  quiet: "모두 조용한 방으로",
};

/** 라벨이 말하지 않은 것 — **나머지는 어떻게 되는가**를 적는다. */
const RECEIVE_HINT: Record<ReceiveMode, string> = {
  all: "처음 보는 주소에서 와도 그대로 목록에 떠요",
  known: "주고받은 적 없는 주소는 조용한 방으로 보내요",
  quiet: "광고든 사람이든 가리지 않고 조용한 방으로 보내요",
};

const RECEIVE_DOT: Record<ReceiveMode, string> = {
  all: "bg-status-active",
  known: "bg-status-awaiting",
  quiet: "bg-status-idle",
};

type SelfProfilePopoverProps = {
  user: CurrentUser;
  /** 팝오버를 여는 요소. 다크 레일 하단의 내 프로필 줄이 들어온다. */
  children: ReactNode;
  onChangeReceiveMode?: (mode: ReceiveMode) => void;
  onRename?: () => void;
  /** 프로필 사진 고르는 창 */
  onChangeAvatar?: () => void;
  onChangePassword?: () => void;
  /** 주소 하나 더 신청하기. 승인을 기다리는 것이 있으면 넘겨도 안 그린다 */
  onAddAddress?: () => void;
  /** 주소 하나를 손보는 창을 연다. 없으면 목록은 읽기 전용으로 남는다 */
  onOpenAddress?: (identityId: string) => void;
  /** 지금 쓰는 주소 = 지금 계정 */
  currentIdentityId?: string | null;
  /** 주소별 안 읽음 */
  unreadByIdentity?: Record<string, number>;
  /** 계정 갈아타기 */
  onSelectIdentity?: (identityId: string) => void;
  /** 최고 관리자에게만 보이는 입구. 없으면 그 줄을 아예 안 그린다 */
  onOpenAdmin?: (() => void) | undefined;
  onSignOut?: () => void;
  /**
   * 이 기기로 알림을 받을지.
   *
   * 콜백 넷을 따로 받지 않고 한 덩이로 받는다. 이 팝오버는 넓은 화면(레일)과
   * 좁은 화면(아래 줄) 두 곳에서 쓰이는데, 넘기는 자리가 갈려 있으면 한쪽만
   * 고치게 된다 — 관리자 줄이 실제로 그래서 폰에서 통째로 빠져 있었다.
   */
  push?: PushControl;
};

/** 이 기기 알림. 상태와 조작을 한 덩이로 넘긴다 */
export type PushControl = {
  /** 서버에 키가 없거나 브라우저가 못 하면 false. 그러면 줄 자체를 안 그린다 */
  available: boolean;
  state: PushState;
  onTurnOn: () => void;
  onTurnOff: () => void;
};

/** 시안 02-2 · 내 프로필 누르면 */
export function SelfProfilePopover({
  user,
  children,
  onChangeReceiveMode,
  onRename,
  onChangeAvatar,
  onChangePassword,
  onAddAddress,
  onOpenAddress,
  currentIdentityId,
  unreadByIdentity,
  onSelectIdentity,
  onOpenAdmin,
  onSignOut,
  push,
}: SelfProfilePopoverProps) {
  const [pickingMode, setPickingMode] = useState(false);
  /**
   * 좁은 화면에서 주소 목록은 **아래로** 연다.
   *
   * 옆으로 여는 것은 옆에 자리가 있을 때 얘기다. 폰에서는 팝오버가 이미 화면
   * 폭을 거의 다 쓰고 있어서, 오른쪽으로 열면 그대로 화면 밖으로 잘려 나간다.
   */
  const narrow = useMediaQuery(NARROW_QUERY);
  const [showingAddresses, setShowingAddresses] = useState(false);
  const coverColor = `var(--color-avatar-${(user.colorIndex % 11) + 1})`;

  /**
   * **다른 계정에** 쌓인 안 읽음.
   *
   * 계정이 나뉜 앱에서 제일 흔한 사고가 "저쪽에 온 메일을 몇 주 뒤에 발견"이다.
   * 목록은 지금 계정만 보여주므로, 접혀 있는 이 줄이 나머지를 대신 말해야 한다.
   */
  const elsewhere = Object.entries(unreadByIdentity ?? {}).reduce(
    (sum, [id, count]) => (id === currentIdentityId ? sum : sum + count),
    0,
  );

  const addressList = (
    <AddressList
      addresses={user.addresses}
      heading={!narrow}
      currentId={currentIdentityId}
      unread={unreadByIdentity}
      onSelect={onSelectIdentity}
      onAddAddress={onAddAddress}
      onOpenAddress={onOpenAddress}
    />
  );

  return (
    <Popover>
      <Popover.Trigger asChild>{children}</Popover.Trigger>

      <Popover.Content
        side={narrow ? "top" : "right"}
        align={narrow ? "start" : "end"}
        sideOffset={12}
        collisionPadding={12}
        className="w-74 max-w-[calc(100vw-24px)]"
      >
        <div className="h-15" style={{ backgroundColor: coverColor }} />

        <div className="-mt-6.5 px-4 pb-3.5">
          <Avatar
            size="2xl"
            initials={user.initials}
            colorIndex={user.colorIndex}
            imageUrl={user.avatarUrl}
            label={user.displayName}
            className="border-surface border-4"
          />

          <div className="mt-2.5 flex flex-col gap-0.75">
            <div className="text-ink text-lg font-bold tracking-tight">
              {user.displayName}
            </div>
            <div className="text-muted-2 text-[13px]">{user.primaryAddress}</div>
          </div>

          {/*
            무엇을 고르는 자리인지 이름을 붙인다. 값만 덩그러니 있으면 지금 상태를
            알리는 문구인지 누를 수 있는 것인지 구분되지 않는다.
          */}
          <p className="text-muted-3 mt-3.5 mb-1 text-[11px] font-semibold">
            새 메일
          </p>

          <button
            type="button"
            onClick={() => setPickingMode((open) => !open)}
            aria-expanded={pickingMode}
            aria-label={`새 메일 — ${RECEIVE_LABEL[user.receiveMode]}`}
            className={`bg-surface-3 hover:bg-chip active:bg-line-soft flex w-full items-center gap-2 rounded-[10px] px-3 py-2.25 ${PRESS_FLAT} ${FOCUS}`}
          >
            <span
              className={`size-2 shrink-0 rounded-full ${RECEIVE_DOT[user.receiveMode]}`}
            />
            <span className="text-body flex-1 text-left text-[13px]">
              {RECEIVE_LABEL[user.receiveMode]}
            </span>
            <ChevronDown
              className={`text-muted-3 size-3.5 transition-transform ${pickingMode ? "rotate-180" : ""}`}
            />
          </button>

          {pickingMode ? (
            <div
              className={`border-line-soft mt-1.5 flex flex-col overflow-hidden rounded-[10px] border ${DROP_IN}`}
            >
              {RECEIVE_MODES.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => {
                    setPickingMode(false);
                    if (mode !== user.receiveMode) onChangeReceiveMode?.(mode);
                  }}
                  // 수신 방식은 조용히 바뀌면 안 되는 설정이다. 키보드로 훑을 때
                  // 지금 어디에 있는지 보여야 잘못 고르지 않는다.
                  aria-pressed={mode === user.receiveMode}
                  className={`hover:bg-surface-3 active:bg-chip flex items-start gap-2 px-3 py-2 text-left ${PRESS_FLAT} ${FOCUS}`}
                >
                  <span
                    className={`mt-1.5 size-2 shrink-0 rounded-full ${RECEIVE_DOT[mode]}`}
                  />
                  <span className="flex-1">
                    <span className="text-ink block text-[13px]">
                      {RECEIVE_LABEL[mode]}
                    </span>
                    <span className="text-muted-3 block text-[12px]">
                      {RECEIVE_HINT[mode]}
                    </span>
                  </span>
                  {mode === user.receiveMode ? (
                    <Check className="text-muted-2 mt-1 size-3.5 shrink-0" />
                  ) : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="border-line-soft border-t py-1.5">
          {/*
            알림은 **이 기기 이야기**다. 계정 설정이 아니라 브라우저가 들고 있는
            구독이라, 폰에서 켜도 노트북은 그대로다. 그래서 이름에 "이 기기"를
            넣는다 — 안 넣으면 한 번 켠 사람이 다른 기기에서도 켜진 줄 안다.
          */}
          {push?.available ? (
            <ListRow
              size="sm"
              leading={push.state === "on" ? <Bell /> : <BellOff />}
              // 막힌 상태는 앱이 되돌릴 수 없다. 눌리게 두면 눌러도 아무 일이
              // 없는 줄이 되고, 사용자는 앱이 고장 난 줄 안다.
              disabled={push.state === "denied" || push.state === "busy"}
              onClick={push.state === "on" ? push.onTurnOff : push.onTurnOn}
              trailing={
                push.state === "denied" ? (
                  <span className="text-muted-3 shrink-0 text-[11px]">
                    브라우저에서 막힘
                  </span>
                ) : null
              }
            >
              {push.state === "on" ? "이 기기 알림 끄기" : "이 기기 알림 받기"}
            </ListRow>
          ) : null}

          <ListRow size="sm" leading={<Pencil />} onClick={onRename}>
            내 이름 바꾸기
          </ListRow>
          <ListRow size="sm" leading={<ImagePlus />} onClick={onChangeAvatar}>
            프로필 사진 바꾸기
          </ListRow>
          <ListRow size="sm" leading={<KeyRound />} onClick={onChangePassword}>
            비밀번호 바꾸기
          </ListRow>
        </div>

        <div className="border-line-soft border-t py-1.5">
          {narrow ? (
            /*
              좁은 화면에서는 **이 자리에서 펼친다.**

              팝오버 위에 팝오버를 또 띄우면 390px에서는 뜰 자리가 없어서 부모를
              덮어버린다. 실제로 내 이름과 주소가 자식 팝오버에 가려 안 보였다.
              화살표도 아래를 가리키도록 바꾼다 — 오른쪽을 가리켜 놓고 아래로
              펼치면 화살표가 거짓말을 하는 셈이다.
            */
            <>
              <ListRow
                size="sm"
                leading={<Inbox />}
                count={user.addresses.length}
                onClick={() => setShowingAddresses((open) => !open)}
                aria-expanded={showingAddresses}
                trailing={
                  <span className="flex shrink-0 items-center gap-1.5">
                    {elsewhere > 0 ? <Badge count={elsewhere} size="sm" /> : null}
                    <ChevronDown
                      className={`text-faint size-4 transition-transform ${
                        showingAddresses ? "rotate-180" : ""
                      }`}
                    />
                  </span>
                }
              >
                내 주소
              </ListRow>
              {showingAddresses ? (
                <div className={`flex flex-col pt-1 ${DROP_IN}`}>{addressList}</div>
              ) : null}
            </>
          ) : (
            /*
              넓은 화면에서는 **옆으로** 연다. 여기 끼워 넣으면 주소가 늘수록
              아래에 있던 로그아웃이 계속 밀려 내려가 자리를 못 외운다.
            */
            <Popover>
              <Popover.Trigger asChild>
                <ListRow
                  size="sm"
                  leading={<Inbox />}
                  count={user.addresses.length}
                  // 화살표 대신 안 읽음을 세운다. 여는 방향은 옆이라 화살표가
                  // 있으면 좋지만, 다른 계정에 메일이 왔다는 사실이 먼저다.
                  trailing={
                    elsewhere > 0 ? (
                      <Badge count={elsewhere} size="sm" />
                    ) : (
                      <ChevronRight className="text-faint size-4 shrink-0" />
                    )
                  }
                >
                  내 주소
                </ListRow>
              </Popover.Trigger>

              <Popover.Content
                side="right"
                align="start"
                sideOffset={6}
                collisionPadding={12}
                className="flex w-64 flex-col py-1.5"
              >
                {addressList}
              </Popover.Content>
            </Popover>
          )}
        </div>

        {onOpenAdmin ? (
          <div className="border-line-soft border-t py-1.5">
            <ListRow size="sm" leading={<ShieldCheck />} chevron onClick={onOpenAdmin}>
              받아주기
            </ListRow>
          </div>
        ) : null}

        <div className="border-line-soft border-t py-1.5 pb-2">
          <ListRow size="sm" tone="danger" leading={<LogOut />} onClick={onSignOut}>
            로그아웃
          </ListRow>
        </div>
      </Popover.Content>
    </Popover>
  );
}

/** 자리를 옮겨 붙일 수 있게 목록만 따로 뺐다 — 좁은 화면은 끼워 넣고, 넓은 화면은 띄운다. */
function AddressList({
  addresses,
  heading = false,
  currentId,
  unread,
  onSelect,
  onAddAddress,
  onOpenAddress,
}: {
  addresses: CurrentUser["addresses"];
  /**
   * 머리글을 붙일지.
   *
   * 띄워서 볼 때만 필요하다. 행에서 떨어져 나와 뜨므로 무엇의 목록인지 스스로
   * 밝혀야 한다. 반대로 그 행 바로 밑에 끼워 넣을 때는 바로 위에 "내 주소"가
   * 이미 적혀 있어서, 붙이면 같은 말이 두 번 나온다.
   */
  heading?: boolean;
  /** 지금 쓰고 있는 주소. 갈아탈 곳을 고르려면 어디에 있는지부터 보여야 한다 */
  currentId?: string | null;
  /** 주소별 안 읽음. 다른 주소에 쌓인 것을 여기서만 알 수 있다 */
  unread?: Record<string, number>;
  onSelect?: ((identityId: string) => void) | undefined;
  onAddAddress?: (() => void) | undefined;
  onOpenAddress?: ((identityId: string) => void) | undefined;
}) {
  return (
    <>
      {/*
        예전에는 `1/1`처럼 집사 상한을 같이 적었다. 지금은 상한이 신청을 막지
        않으므로(routes/identities.ts) 그대로 두면 다 찬 것처럼 보이는데 그
        아래에는 "주소 늘리기"가 있다 — 화면이 스스로 모순된다. 상한을 보고
        판단하는 것은 관리자이고, 그 숫자는 승인 화면에 있다.
      */}
      {heading ? (
        <p className="text-muted-3 px-3.5 pt-1 pb-1.5 text-[11px] font-semibold">
          내 주소
        </p>
      ) : null}

      <ul className="flex flex-col">
      {addresses.map((entry) => {
        /*
          기다리는 주소를 열린 주소와 똑같이 그리면 안 된다. 똑같이
          보이면 쓸 수 있는 줄 알고 어디에 적어두는데, 승인 전에는 그
          주소로 온 메일을 엣지가 거절한다 — 조용히 사라진다.
        */
        const waiting = entry.status === "pending";
        const refused = entry.status === "rejected";
        // 옛 API는 이 값을 안 보낸다. 없으면 켜진 것으로 본다.
        const off = entry.enabled === false;
        /*
          갈아탈 수 있는 주소인가.

          기다리는 중이거나 꺼둔 주소로 갈아타 봐야 메일이 안 들어온다 — 빈
          목록만 보이고 왜 비었는지는 화면 어디에도 안 적힌다. 그래서 이런
          줄은 고르는 자리가 아니라 **손보는 자리**로만 둔다.
        */
        const selectable = !waiting && !refused && !off;
        const here = selectable && entry.id === currentId;
        const count = unread?.[entry.id] ?? 0;

        return (
          <li key={entry.id} className="flex items-center">
            {/*
              누를 수 있으면 `<button>`이어야 한다(react-component-rules §10).
              고를 수 없는 줄은 버튼으로 그리지 않는다 — 손이 가고 아무 일도
              안 일어난다.
            */}
            {selectable && onSelect ? (
              <button
                type="button"
                onClick={() => onSelect(entry.id)}
                // 지금 있는 곳을 색으로만 알리지 않는다(§10). 체크가 같이 선다.
                aria-current={here ? "true" : undefined}
                // 줄을 누르면 갈아탄다는 것을 이름으로도 말한다. 옆 단추가
                // "…손보기"라 주소만으로는 둘이 구분되지 않는다.
                aria-label={here ? `${entry.address} — 지금 쓰는 주소` : `${entry.address} 로 갈아타기`}
                className={`hover:bg-surface-3 active:bg-chip flex min-w-0 flex-1 items-center gap-2 py-1.25 pl-3.5 text-left text-[13px] ${PRESS_FLAT} ${FOCUS}`}
              >
                <span
                  className={`min-w-0 flex-1 truncate ${here ? "text-ink font-semibold" : "text-body"}`}
                >
                  {entry.address}
                </span>
                {/*
                  다른 주소에 쌓인 안 읽음. 이게 없으면 갈아타 보기 전에는 저쪽에
                  메일이 온 줄 모른다 — 계정이 나뉜 앱에서 제일 흔한 사고다.
                  지금 있는 곳은 목록에 그대로 떠 있으므로 세지 않는다.
                */}
                {!here && count > 0 ? (
                  <Badge count={count} size="sm" />
                ) : null}
                {here ? <Check className="text-muted-2 size-3.5 shrink-0" /> : null}
              </button>
            ) : (
              <span className="flex min-w-0 flex-1 items-center gap-2 py-1.25 pl-3.5 text-[13px]">
                <span className="text-muted-3 min-w-0 flex-1 truncate">
                  {entry.address}
                </span>
                {waiting ? (
                  <span className="text-muted-3 shrink-0 text-[12px]">
                    기다리는 중
                  </span>
                ) : refused ? (
                  <span className="text-danger shrink-0 text-[12px]">거절됨</span>
                ) : (
                  // 꺼둔 것은 내가 한 일이라 "거절됨"과 같은 색으로 두면 안 된다.
                  <span className="text-muted-3 shrink-0 text-[12px]">꺼짐</span>
                )}
              </span>
            )}

            {/*
              손보기를 줄 전체에서 떼어 낸다. 줄을 누르는 것은 **갈아타기**이고,
              끄기·지우기는 그 옆의 작은 단추다. 한 줄에 둘을 겹쳐 두면 누를 때
              무엇이 일어날지 알 수 없다.
            */}
            {onOpenAddress ? (
              <IconButton
                size="sm"
                label={`${entry.address} 손보기`}
                className="mr-1.5 shrink-0"
                onClick={() => onOpenAddress(entry.id)}
              >
                <Settings2 />
              </IconButton>
            ) : null}
          </li>
        );
      })}
    </ul>

    {/*
      여기가 주소를 늘리는 유일한 입구다. 예전에는 목록만 있고 다음 걸음이
      없어서, 주소를 더 쓰고 싶어도 갈 곳이 없었다.

      **개수로 막지 않는다.** 기다리는 것이 있어도 계속 열어둔다 — 문지기는
      숫자가 아니라 사람이고, 여기서 미리 막으면 관리자가 볼 기회 자체가 없다.
    */}
    {onAddAddress ? (
      <div className="border-line-soft mt-1.5 border-t pt-1.5">
        <ListRow size="sm" leading={<Plus />} onClick={onAddAddress}>
          주소 늘리기
        </ListRow>
      </div>
    ) : null}
    </>
  );
}
